import { useRef, useState } from "react"
import { useAuth } from "../auth"
import type { Metrics } from "../api/types"
import { VIA_LABEL } from "../lib/delivery"
import { dur } from "../lib/time"
import { currentProject, goHome, toggleLeft, toggleRight } from "../store/actions"
import { useFailures, useOpenRequests } from "../store/derived"
import { getState, savePrefs, setUi, useStore } from "../store/store"
import { Popover, Tip } from "./ui"

const SOCK_LABEL = {
	live: "Live — realtime connected",
	connecting: "Connecting…",
	reconnecting: "Reconnecting…",
	offline: "Offline — retrying",
} as const

export function TopBar() {
	const me = useStore(s => s.me)
	const project = useStore(s => currentProject(s))
	const activity = useStore(s => s.activity)
	const socket = useStore(s => (s.projectId && s.sockets[s.projectId]) || "connecting")
	const agents = useStore(s => s.agents)
	const metrics = useStore(s => s.metrics)
	const open = useOpenRequests()
	const failures = useFailures()
	const auth = useAuth()
	const userBtn = useRef<HTMLButtonElement>(null)
	const [userMenu, setUserMenu] = useState(false)

	const list = Object.values(agents).filter(a => a.state !== "ended")
	const n = (st: string) => list.filter(a => a.state === st).length
	const blockers = open.filter(m => m.kind === "blocker").length
	const leftOpen = useStore(s => s.ui.leftOpen)
	const otherActivity = Object.entries(activity).some(
		([pid, c]) => pid !== project?.id && c > 0,
	)
	const p50 = metrics?.overall?.present_p50 ?? null
	const p95 = metrics?.overall?.present_p95 ?? null

	const showAgents = () => {
		if (!getState().ui.rightOpen) toggleRight()
		requestAnimationFrame(() => document.querySelector<HTMLElement>(".agent")?.focus())
	}

	return (
		<header className="top">
			<a
				className="brand"
				href="/"
				title="All projects"
				onClick={e => {
					if (e.metaKey || e.ctrlKey) return
					e.preventDefault()
					goHome()
				}}
			>
				<span className="brand-mark" aria-hidden />
				walkie
			</a>
			{project ? (
				<>
					<span className="crumb-sep" aria-hidden>
						/
					</span>
					<a
						className="crumb"
						href="/"
						onClick={e => {
							if (e.metaKey || e.ctrlKey) return
							e.preventDefault()
							goHome()
						}}
					>
						Projects
					</a>
				</>
			) : (
				<span className="grow" />
			)}
			{!leftOpen && project && (
				<button
					className="proj-btn"
					onClick={toggleLeft}
					title="Show the sidebar ([)"
					aria-label={`Project ${project.name}. Show the sidebar`}
				>
					<b className="ell">{project.name}</b>
					{otherActivity && (
						<span className="activity-dot" aria-label="activity in other projects" />
					)}
				</button>
			)}
			{project && (
				<>
					<button
						className="search-btn"
						onClick={() => setUi({ palette: true })}
						aria-label="Search and jump (Ctrl K or /)"
					>
						<span aria-hidden>⌕</span>
						<span>Search, jump to room, agent, request…</span>
						<kbd>⌘K</kbd>
					</button>

					<nav className="counters" aria-label="Project status">
						<Tip content="Agents active · idle · offline">
							<button
								className="ctr"
								onClick={showAgents}
								aria-label={`${n("active")} active, ${n("idle")} idle, ${n("offline")} offline agents`}
							>
								<span className="sdot s-active" aria-hidden />
								<b>{n("active")}</b>
								<span className="sdot s-idle" aria-hidden style={{ marginLeft: 4 }} />
								<b>{n("idle")}</b>
								<span className="sdot s-offline" aria-hidden style={{ marginLeft: 4 }} />
								<b>{n("offline")}</b>
								<span className="lbl-long">agents</span>
							</button>
						</Tip>
						<span className="ctr-sep" />
						<button
							className="ctr"
							onClick={() => setUi({ filter: "requests" })}
							aria-label={`${open.length} open requests; show them`}
						>
							<b>{open.length}</b> open
						</button>
						<button
							className={"ctr" + (blockers ? " alert" : "")}
							onClick={() => setUi({ filter: "blockers" })}
							aria-label={`${blockers} blockers; show them`}
						>
							<b>{blockers}</b> {blockers === 1 ? "blocker" : "blockers"}
						</button>
						<button
							className={"ctr" + (failures.length ? " alert" : "")}
							onClick={() => setUi({ filter: "failing" })}
							aria-label={`${failures.length} delivery failures; show them`}
						>
							<b>{failures.length}</b> <span className="lbl-long">delivery</span> failed
						</button>
						<span className="ctr-sep" />
						<Tip content={() => <LatencyTable metrics={metrics} />}>
							<span
								className={"ctr" + (Number(p95) > 30_000 ? " warn" : "")}
								tabIndex={0}
								aria-label={`Present latency p50 ${dur(p50)}, p95 ${dur(p95)}`}
							>
								<span className="lbl-long">present</span> p50 <b>{dur(p50)}</b> p95{" "}
								<b>{dur(p95)}</b>
							</span>
						</Tip>
					</nav>
				</>
			)}
			{project && (
				<>
					<button className="btn btn-xs" onClick={() => setUi({ connect: true })}>
						Connect
					</button>
					<Tip content={SOCK_LABEL[socket]}>
						<span
							className="ctr"
							role="status"
							aria-label={SOCK_LABEL[socket]}
							tabIndex={0}
						>
							<span className={"sockdot sock-" + socket} />
						</span>
					</Tip>
				</>
			)}
			<button
				ref={userBtn}
				className="icon-btn"
				aria-haspopup="menu"
				aria-expanded={userMenu}
				aria-label={`Account: ${me?.user.handle ?? ""}`}
				onClick={() => setUserMenu(!userMenu)}
			>
				<span className="avatar">
					{(me?.user.name || me?.user.handle || "?")[0]?.toUpperCase()}
				</span>
			</button>
			{userMenu && (
				<Popover
					anchor={userBtn.current}
					onClose={() => setUserMenu(false)}
					label="Account"
					align="right"
				>
					<div className="menu-label">
						{me?.user.name} · @{me?.user.handle}
					</div>
					{me?.user.email && (
						<div className="dim" style={{ padding: "0 8px 4px", fontSize: 11 }}>
							{me.user.email}
						</div>
					)}
					<div className="menu-sep" />
					<button
						className="menu-item"
						role="menuitem"
						onClick={() => (setUserMenu(false), setUi({ settings: true }))}
					>
						Project settings
					</button>
					<button
						className="menu-item"
						role="menuitem"
						onClick={() => (setUserMenu(false), setUi({ newProject: true }))}
					>
						New project
					</button>
					<button
						className="menu-item"
						role="menuitem"
						onClick={() => (setUserMenu(false), setUi({ connect: true }))}
					>
						Devices & connections
					</button>
					<button
						className="menu-item"
						role="menuitem"
						onClick={() => (setUserMenu(false), setUi({ shortcuts: true }))}
					>
						<span className="grow">Keyboard shortcuts</span>
						<kbd>?</kbd>
					</button>
					<div className="menu-sep" />
					<button
						className="menu-item"
						role="menuitem"
						onClick={() => {
							setUserMenu(false)
							savePrefs({ projectId: undefined })
							void auth.signOut()
						}}
					>
						Sign out
					</button>
				</Popover>
			)}
		</header>
	)
}

const CONF_SHORT: Record<string, string> = {
	transcript: "transcript",
	"tool-result": "tool result",
}

/** Hover table: send → presented latency per presentation path (7 days). */
function LatencyTable({ metrics }: { metrics: Metrics | null }) {
	const rows = [...(metrics?.byVia ?? [])].sort((a, b) => b.n - a.n).slice(0, 8)
	return (
		<div className="lat-tip">
			<div className="lat-h">send → presented by path · 7d</div>
			{rows.length ? (
				<table>
					<thead>
						<tr>
							<th>via</th>
							<th>confirmed</th>
							<th className="num">n</th>
							<th className="num">p50</th>
							<th className="num">p95</th>
						</tr>
					</thead>
					<tbody>
						{rows.map(v => {
							const hook = !!v.confirmed && !CONF_SHORT[v.confirmed]
							return (
								<tr key={`${v.via}-${v.confirmed}`}>
									<td>{v.via ? (VIA_LABEL[v.via] ?? v.via) : "unknown"}</td>
									<td className={hook ? "hook" : undefined}>
										{v.confirmed
											? (CONF_SHORT[v.confirmed] ?? "hook only")
											: "unconfirmed"}
									</td>
									<td className="num">{v.n}</td>
									<td className="num">{dur(v.p50)}</td>
									<td className={"num" + (Number(v.p95) > 30_000 ? " slow" : "")}>
										{dur(v.p95)}
									</td>
								</tr>
							)
						})}
					</tbody>
				</table>
			) : (
				<div className="dim2">No presentations yet.</div>
			)}
		</div>
	)
}
